/**
 * Modal CRUD para gerenciamento de Blacklist LGPD
 * Permite adicionar, visualizar e remover contatos da blacklist
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useBlacklist, BlacklistEntry, BlockReason, BLOCK_REASON_LABELS } from '@/hooks/useBlacklist';
import { formatPhoneForDisplay } from '@/lib/phone-utils';
import { parseCSVLine, detectDelimiter } from '@/lib/csv-utils';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';

// Componentes UI
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Ícones
import {
  UserX,
  Plus,
  Trash2,
  Search,
  Upload,
  Shield,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  FileText
} from 'lucide-react';

// =====================================================
// TIPOS
// =====================================================

interface BlacklistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// =====================================================
// COMPONENTE
// =====================================================

export default function BlacklistDialog({ open, onOpenChange }: BlacklistDialogProps) {
  const {
    entries,
    isLoading,
    totalCount,
    fetchBlacklist,
    addToBlacklist,
    removeFromBlacklist,
    importBlacklistCSV
  } = useBlacklist();
  
  // Estados
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 20;
  
  // Estados do formulário de adição
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newName, setNewName] = useState('');
  const [newReason, setNewReason] = useState<BlockReason>('opt_out');
  const [isAdding, setIsAdding] = useState(false);
  
  // Estados de importação CSV
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Estado de confirmação de exclusão
  const [deleteEntry, setDeleteEntry] = useState<BlacklistEntry | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Carregar dados quando modal abre
  useEffect(() => {
    if (open) {
      fetchBlacklist(page, pageSize, searchQuery);
    }
  }, [open, page, searchQuery, fetchBlacklist]);
  
  // Resetar página ao buscar
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);
  
  // Handlers
  const handleAdd = async () => {
    if (!newPhone.trim()) return;
    
    setIsAdding(true);
    const success = await addToBlacklist(newPhone, newName || undefined, newReason);
    setIsAdding(false);
    
    if (success) {
      setNewPhone('');
      setNewName('');
      setNewReason('opt_out');
      setShowAddForm(false);
      fetchBlacklist(0, pageSize, searchQuery);
      setPage(0);
    }
  };
  
  const handleDelete = async () => {
    if (!deleteEntry) return;
    
    setIsDeleting(true);
    const success = await removeFromBlacklist(deleteEntry.id);
    setIsDeleting(false);
    
    if (success) {
      setDeleteEntry(null);
      fetchBlacklist(page, pageSize, searchQuery);
    }
  };
  
  const handleCSVUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsImporting(true);
    
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(line => line.trim());
      
      if (lines.length < 2) {
        throw new Error('CSV deve ter pelo menos um cabeçalho e uma linha de dados');
      }
      
      // Detectar delimitador e parsear cabeçalho
      const delimiter = detectDelimiter(lines[0]);
      const headers = parseCSVLine(lines[0], delimiter).map(h => h.toLowerCase().trim());
      
      // Encontrar índices das colunas
      const phoneIndex = headers.findIndex(h => h === 'telefone' || h === 'phone');
      const nameIndex = headers.findIndex(h => h === 'nome' || h === 'name');
      const reasonIndex = headers.findIndex(h => h === 'motivo' || h === 'reason');
      
      if (phoneIndex === -1) {
        throw new Error('CSV deve ter coluna "telefone" ou "phone"');
      }
      
      // Parsear linhas de dados
      const contacts: Array<{ phone: string; name?: string; reason?: BlockReason }> = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i], delimiter);
        const phone = values[phoneIndex]?.trim();
        
        if (phone) {
          contacts.push({
            phone,
            name: nameIndex !== -1 ? values[nameIndex]?.trim() : undefined,
            reason: reasonIndex !== -1 ? (values[reasonIndex]?.trim() as BlockReason) : undefined
          });
        }
      }
      
      if (contacts.length === 0) {
        throw new Error('Nenhum contato válido encontrado no CSV');
      }
      
      // Importar
      await importBlacklistCSV(contacts);
      fetchBlacklist(0, pageSize, searchQuery);
      setPage(0);
      
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao processar CSV';
      logger.error('[BlacklistDialog] Erro na importação:', errorMsg);
      // Exibe toast de erro para o usuário
      toast.error(errorMsg);
    } finally {
      setIsImporting(false);
      // Resetar input para permitir reimportação do mesmo arquivo
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Paginação
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = page < totalPages - 1;
  const hasPrevPage = page > 0;
  
  // Formatar data
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  // =====================================================
  // RENDER
  // =====================================================
  
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Blacklist LGPD
              {totalCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {totalCount} {totalCount === 1 ? 'contato' : 'contatos'}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Contatos nesta lista não receberão campanhas de marketing (adequação LGPD)
            </DialogDescription>
          </DialogHeader>
          
          {/* Alerta informativo */}
          <Alert>
            <UserX className="h-4 w-4" />
            <AlertDescription>
              Contatos adicionados aqui serão automaticamente excluídos de todas as campanhas.
              Ao importar listas CSV, contatos na blacklist serão sinalizados e não serão salvos.
            </AlertDescription>
          </Alert>
          
          {/* Barra de ações */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Buscar por telefone ou nome..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-8 text-sm"
              />
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Adicionar
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isImporting}
            >
              {isImporting ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-1" />
              )}
              Importar CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              className="hidden"
              onChange={handleCSVUpload}
            />
          </div>
          
          {/* Formulário de adição */}
          {showAddForm && (
            <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Telefone *</Label>
                  <Input
                    placeholder="55 11 99999-9999"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Nome (opcional)</Label>
                  <Input
                    placeholder="Nome do contato"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Motivo</Label>
                  <Select value={newReason} onValueChange={(v) => setNewReason(v as BlockReason)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(BLOCK_REASON_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowAddForm(false)}>
                  Cancelar
                </Button>
                <Button size="sm" onClick={handleAdd} disabled={isAdding || !newPhone.trim()}>
                  {isAdding ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-1" />
                  )}
                  Adicionar
                </Button>
              </div>
            </div>
          )}
          
          <Separator />
          
          {/* Tabela de entradas */}
          <ScrollArea className="flex-1 min-h-[300px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <UserX className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Blacklist vazia</p>
                <p className="text-sm">Adicione contatos que não devem receber campanhas</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-mono">
                        {formatPhoneForDisplay(entry.phone)}
                      </TableCell>
                      <TableCell>{entry.name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {BLOCK_REASON_LABELS[entry.reason as BlockReason] || entry.reason}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(entry.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeleteEntry(entry)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
          
          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Página {page + 1} de {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p - 1)}
                  disabled={!hasPrevPage}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasNextPage}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Dica de formato CSV */}
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <FileText className="h-3 w-3" />
            Formato CSV: telefone, nome (opcional), motivo (opcional)
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={!!deleteEntry} onOpenChange={(open) => !open && setDeleteEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Remover da Blacklist
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover <strong>{deleteEntry?.name || formatPhoneForDisplay(deleteEntry?.phone)}</strong> da blacklist?
              Este contato poderá receber campanhas novamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Removendo...' : 'Remover'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
